/**
 * The easter-egg registry.
 *
 * Deliberately narrow in scope: an egg may add a line of text or a flourish of
 * animation, and nothing else. None of them can touch the decision — the
 * outcome is drawn before any of this runs, and no egg is given a way to read
 * or change it. That is a hard product rule, not an implementation detail.
 */

import type { Rng } from './random.ts';

export type EggStage = 'idle' | 'countdown' | 'walking' | 'result';

export interface EasterEgg {
  id: string;
  stage: EggStage;
  /** 0–1. Roll once per stage entry. */
  chance: number;
  text: string;
}

export const EASTER_EGGS: readonly EasterEgg[] = [
  { id: 'knows', stage: 'result', chance: 0.04, text: 'Jeff knows.' },
  { id: 'process', stage: 'result', chance: 0.04, text: 'Trust the process.' },
  { id: 'ta-complete', stage: 'result', chance: 0.035, text: 'Technical analysis complete.' },
  {
    id: 'no-indicators',
    stage: 'result',
    chance: 0.025,
    text: 'No indicators were harmed in the making of this trade.',
  },
  { id: 'liquidity', stage: 'result', chance: 0.03, text: 'Jeff has reviewed the liquidity.' },
  { id: 'risk-mgmt', stage: 'result', chance: 0.025, text: 'Risk management has left the chat.' },
  { id: 'unanimous', stage: 'result', chance: 0.02, text: 'Decision was unanimous.' },

  // There were four more here, for the countdown and the walk. They surfaced
  // through the rolling caption under the picture, and that caption is gone —
  // the camera HUD that replaced it shows a tally light, a camera name and a
  // clock, and nothing that talks. Kept out rather than left in the registry
  // firing into nowhere; the stage type still allows them if a surface returns.
] as const;

/**
 * Rolls the eggs for a stage and returns at most one.
 *
 * Takes an injectable RNG so tests are deterministic. The cosmetic PRNG is fine
 * here; nothing about an egg is security-relevant.
 *
 * Note that `chance` is a per-roll probability evaluated in registry order, not
 * an independent share: the first egg to hit wins, so a later egg only gets a
 * roll if every earlier one missed. Its *effective* rate is therefore lower than
 * its `chance`. That is fine for a joke — the eggs are ordered by how much they
 * deserve to land — but it is not what the numbers look like they say.
 */
export function rollEasterEgg(stage: EggStage, rng: Rng, enabled: boolean): EasterEgg | null {
  if (!enabled) return null;
  for (const egg of EASTER_EGGS) {
    if (egg.stage !== stage) continue;
    if (rng() < egg.chance) return egg;
  }
  return null;
}
